import React, { createContext, useEffect, useRef, useState } from "react";
import { getLiveEventsByToday, mainScreen, sportData } from '../services/player/SportService.tsx';
import { BannerSport, LeagueLiveConfig, LeagueModel, LiveEvent, SlotConfig, SportModel } from "../models/Sport.tsx";
import { futureGames, scheduleLive } from "../services/player/apis/Schelude.tsx";
import { Schedule } from "../models/Schedule.tsx";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { Future } from "../models/MyBetModel.tsx";
import { applyTeaserPointsToOdd, filterPreferredSportsbookOdds, getClientTimezone, getUniqueMarketIdsByLeague, preferPrimarySportsbookOdds } from "../helpers/functions.tsx";
import { findLeagueMarket, getLiveScheduleDateRange, getScheduleDateRange, pickLiveSlateGames, PREGAME_SCHEDULE_EXTRA_DAYS } from "../helpers/scheduleDateRange.ts";
import {
  GOLF_SPORT_NAME,
  isGolfFuturesLiveWindow,
  isPgaCommercialLiveSelection,
  PGA_LEAGUE_NAME,
} from "../helpers/golfFuturesLive.ts";
import { MarketsByLeague } from "../models/GamesResults.tsx";
import { trace } from "firebase/performance";
import { perf } from "../services/Firebase.js";
import { useUser } from "./UserContext.tsx";

const prefix = "gs://jocksportsdev.appspot.com/";
const resolveGsUrl = async (gsUrl: string, storage = getStorage()): Promise<string> => {
  if (!gsUrl?.startsWith("gs://")) return gsUrl ?? "";
  try {
    return await getDownloadURL(ref(storage, gsUrl.replace(prefix, "")));
  } catch {
    return "";
  }
};

// const resolveLogosForSports = async (sports: SportModel[]): Promise<SportModel[]> => {
//   const storage = getStorage();
//   const gsUrls = new Set<string>();

//   for (const sport of sports) {
//     if (sport.logo?.startsWith("gs://")) gsUrls.add(sport.logo);
//     for (const league of sport.leagues ?? []) {
//       if (league.logo?.startsWith("gs://")) gsUrls.add(league.logo);
//     }
//   }

//   const resolved = new Map<string, string>();
//   await Promise.all(
//     [...gsUrls].map(async (url) => {
//       resolved.set(url, await resolveGsUrl(url, storage));
//     })
//   );

//   const mapLogo = (logo?: string) =>
//     logo?.startsWith("gs://") ? (resolved.get(logo) ?? "") : (logo ?? "");

//   return sports.map((sport) => ({
//     ...sport,
//     logo: mapLogo(sport.logo),
//     leagues: sport.leagues?.map((league) => ({
//       ...league,
//       logo: mapLogo(league.logo),
//     })),
//   }));
// };

const SportContext = createContext<any>(null);

const SportProvider = ({ children }: { children: React.ReactNode }) => {

  const { firebaseUser, user, isLoading: authLoading } = useUser();
  const [showOptions, setShowOptions] = useState<boolean[]>([]);
  const [error, setError] = useState(null);
  const [activeGames, setActiveGames] = useState<Schedule[]>([]);
  const [commercialGames, setCommercialGames] = useState<Schedule[]>([]);
  const [commercialLeagueOrder, setCommercialLeagueOrder] = useState<string[]>([]);
  const [activeGamesTeaser, setActiveGamesTeaser] = useState<Schedule[]>([]);
  const [gameEspecial, setGameEspecial] = useState<Schedule[]>([]);
  const [activeFutureGames, setActiveFutureGames] = useState<Future[]>([]);
  const [commercialFutureGames, setCommercialFutureGames] = useState<Future[]>([]);
  const [activeFutureIndividual, setActiveFutureIndividual] = useState<Future[]>([]);
  const [sports, setSports] = useState<SportModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkedLeagues, setCheckedLeagues] = useState<Record<string, boolean>>({});
  const checkedLeaguesRef = useRef<Record<string, boolean>>({});
  const loadGamesGenRef = useRef<Record<string, number>>({});
  const loadGamesInFlightRef = useRef(0);
  const [selectedGames, setSelectedGames] = useState(false);
  const boxesRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isParlay, setIsParlay] = useState(false);
  const [showSports, setShowSports] = useState(true);
  const [showHomeButton, setShowHomeButton] = useState(false);
  const [marketsByLeague, setMarketsByLeague] = useState<MarketsByLeague>({});
  const [leagueLive, setLeagueLive] = useState<LiveEvent>();
  const [leagueLiveconfig, setLeagueLiveconfig] = useState<LeagueLiveConfig[]>([]);
  const [bannerSportConfig, setBannerSportConfig] = useState<BannerSport[]>(() => {
    try {
      const stored = localStorage.getItem("bannerSportConfig");
      return stored ? (JSON.parse(stored) as BannerSport[]) : [];
    } catch {
      return [];
    }
  });
  const [marketOrderFromDB, setMarketOrderFromDB] = useState<SlotConfig[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [checkedTeaserLeagues, setCheckedTeaserLeagues] = useState<Record<string, boolean>>({});
  const [noDataTeaser, setNoDataTeaser] = useState(false);
  const [selectedGamesTeaser, setSelectedGamesTeaser] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [errorMessages, setErrorMessage] = useState("");
  const [lastLeagueKey, setLastLeagueKey] = useState<string | null>(null);
  const [marketsByLeagueKey, setMarketsByLeagueKey] = useState<Record<string, any>>({});

  const loadSports = async () => {
    setLoading(true);
    const t = trace(perf, "loadSports");
    t.start();

    try {
      const optionsData = await sportData();
      // const processedSports = await resolveLogosForSports(optionsData);
      setSports(optionsData);
      setShowOptions(Array(optionsData.length).fill(false));

    } catch (error) {
      console.error("Error loading sports:", error);
    } finally {
      t.stop();
      setLoading(false);
    }
  };

  const loadLiveEvents = async () => {
    try {
      const events = await getLiveEventsByToday();
      setLeagueLive(events ?? { createdAt: new Date(), date: "", lives: [] });
    } catch (error) {
      console.error("Error loading live events:", error);
      setLeagueLive({ createdAt: new Date(), date: "", lives: [] });
    }
  };

  const toggleOptions = (key: number) => {
    setShowOptions((prevOptions) => {
      const updatedOptions = [...prevOptions];
      updatedOptions[key] = !updatedOptions[key];
      return updatedOptions;
    });
  };

  const roundAmericanOdds = (odds: number): number => {
    const step = 5;

    if (odds > 0) {
      return Math.floor(odds / step) * step;
    }

    if (odds < 0) {
      return Math.ceil(odds / step) * step;
    }

    return odds;
  };

  const applyHouseRounding = (games: Schedule[]): Schedule[] => {
    return games.map(game => ({
      ...game,
      odds:
        game.odds?.map(odd => ({
          ...odd,
          price:
            typeof odd.price === "number"
              ? roundAmericanOdds(odd.price)
              : odd.price,
        })) ?? [],
    }));
  };

  const applyCheckedLeagues = (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>
  ) => {
    const next = updater(checkedLeaguesRef.current);
    checkedLeaguesRef.current = next;
    setCheckedLeagues(next);
  };

  const setCheckedLeaguesSafe = (
    update: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => {
    if (typeof update === "function") {
      applyCheckedLeagues(update);
      return;
    }
    checkedLeaguesRef.current = update;
    setCheckedLeagues(update);
  };

  const loadGames = async (sport: string, league: LeagueModel, marketKey = "", marketArray = [], type?: "Special",
    isMain?: boolean, startDateAfter?: string, startDateBefore?: string, forceReload = false, sportsbooks?: string[]) => {
    if (!sport || !league?.name) return;

    const t = trace(perf, "loadGames");
    t.start();
    const leagueKey = marketKey ? `${sport}-${league.name}-${marketKey}` : `${sport}-${league.name}`;
    const requestGen = (loadGamesGenRef.current[leagueKey] ?? 0) + 1;
    loadGamesGenRef.current[leagueKey] = requestGen;

    const isCurrentRequest = () => loadGamesGenRef.current[leagueKey] === requestGen;
    const isLeagueStillChecked = () =>
      forceReload || checkedLeaguesRef.current[leagueKey] === true;

    try {
      if (!forceReload) {
        setMarketsByLeagueKey(prev => ({
          ...prev,
          [leagueKey]: marketArray,
        }));
      }

      const isNowChecked = forceReload
        ? true
        : !(checkedLeaguesRef.current[leagueKey] ?? false);

      applyCheckedLeagues(prev => ({
        ...prev,
        [leagueKey]: isNowChecked,
      }));

      if (marketKey === "Live" || marketKey === "1stHalf") {
        setCommercialLeagueOrder(prev => {
          const without = prev.filter(k => k !== leagueKey);
          return isNowChecked ? [...without, leagueKey] : without;
        });
      }

      if (!isNowChecked && !forceReload) {
        if (marketKey === "Live" || marketKey === "1stHalf") {
          setCommercialGames(prev => prev.filter(g => g.leagueKey !== leagueKey));
        } else if (type === "Special") {
          setGameEspecial(prev => prev.filter(g => g.leagueKey !== leagueKey));
        } else {
          setActiveGames(prev => prev.filter(g => g.leagueKey !== leagueKey));
        }
        return;
      }

      // PGA Commercial Live shows futures only — skip straight/live schedule fetch.
      if (isPgaCommercialLiveSelection(sport, league?.name, marketKey)) {
        return;
      }

      loadGamesInFlightRef.current += 1;
      setLoading(true);

      try {
        const marketId = marketArray.length > 0 ? marketArray : "";
        const isLiveMarket = marketKey === "Live" || marketKey === "1stHalf";
        const liveRange = isLiveMarket ? getLiveScheduleDateRange() : null;
        const pregameRange = isLiveMarket
          ? null
          : getScheduleDateRange({
              extraDays: PREGAME_SCHEDULE_EXTRA_DAYS,
              league,
              market: findLeagueMarket(league, marketKey),
            });
        const timeOffset = liveRange?.timeOffset ?? pregameRange?.timeOffset ?? getClientTimezone();
        const queryAfter = liveRange?.startDateAfter ?? pregameRange?.startDateAfter ?? startDateAfter;
        const queryBefore = liveRange?.startDateBefore ?? pregameRange?.startDateBefore ?? startDateBefore;

        let gamesData =
          (await scheduleLive({ sport, league: league.name, marketId, isMain, startDateBefore: queryBefore, startDateAfter: queryAfter, timeOffset, sportsbooks })) ?? [];

        if (isLiveMarket) {
          gamesData = pickLiveSlateGames(gamesData);
        } else {
          gamesData = gamesData.filter(
            game => game.status === "unplayed" && !game.is_live
          );
        }

        if (!isCurrentRequest() || !isLeagueStillChecked()) return;

        const selectedSport = sports.find(s => s.name === sport);
        const selectedLeague = selectedSport?.leagues?.find(l => l.name === league.name);
        const leagueLogo = selectedLeague?.logo || "";

        if (gamesData.length === 0) {
          if (isLiveMarket) {
            setCommercialGames(prev => prev.filter(g => g.leagueKey !== leagueKey));
            return false;
          }
          setErrorMessage(`${league.name} is currently unavailable.`);
          setShowLimitAlert(true);
          setLastLeagueKey(leagueKey);
          applyCheckedLeagues(prev => ({
            ...prev,
            [leagueKey]: false,
          }));
          return false;
        }
        const gamesWithLogos: Schedule[] = gamesData.map(game => ({
          ...game,
          sport: game.sport?.name ? game.sport : { id: sport, name: sport, numerical_id: 0 },
          league: game.league?.name ? game.league : { id: league.name, name: league.name, numerical_id: 0 },
          leagueLogo,
          leagueKey,
          isEmpty: false,
        }));

        const gamesRounded = applyHouseRounding(gamesWithLogos);
        const isCommercial = marketKey === "Live" || marketKey === "1stHalf";

        let finalGames: Schedule[] = gamesRounded;
        const leagueMarketIds = getUniqueMarketIdsByLeague(finalGames);
        setMarketsByLeague(prev => ({ ...prev, ...leagueMarketIds }));

        if (isCommercial) {
          setCommercialGames(prev => {
            if (!isCurrentRequest() || !isLeagueStillChecked()) return prev;
            return [
              ...prev.filter(g => g.leagueKey !== leagueKey),
              ...finalGames,
            ];
          });
        } else if (type === "Special") {
          setGameEspecial(prev => [
            ...prev.filter(g => g.leagueKey !== leagueKey),
            ...finalGames,
          ]);
        } else {
          setActiveGames(prev => [
            ...prev.filter(g => g.leagueKey !== leagueKey),
            ...finalGames,
          ]);
        }
      } finally {
        loadGamesInFlightRef.current = Math.max(0, loadGamesInFlightRef.current - 1);
        if (loadGamesInFlightRef.current === 0) {
          setLoading(false);
        }
      }

    } catch (error: any) {
      setError(error.message || "Error loading games.");
    } finally {
      t.stop();
    }
  };

  const handleCloseLimitAlert = () => {
    setShowLimitAlert(false);

    if (lastLeagueKey) {
      applyCheckedLeagues(prev => ({
        ...prev,
        [lastLeagueKey]: false
      }));
      setCheckedTeaserLeagues(prev => ({
        ...prev,
        [lastLeagueKey]: false
      }));
    }

    setActiveGames(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setCommercialGames(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setGameEspecial(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setActiveFutureGames(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setCommercialFutureGames(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setActiveGamesTeaser(prev => prev.filter(g => g.leagueKey !== lastLeagueKey));
    setNoDataTeaser(false);
    setLastLeagueKey(null);
  };

  const loadGamesFuture = async (sport: string, league: any, futureInput: any) => {
    try {
      const futureArray = Array.isArray(futureInput)
        ? futureInput
        : [futureInput];

      const leagueKey = `${sport}-${league}-${futureArray
        .join("_")
        .replace(/[, ]+/g, "_")}`;

      const isNowChecked = !(checkedLeaguesRef.current[leagueKey] ?? false);

      applyCheckedLeagues(prev => ({
        ...prev,
        [leagueKey]: isNowChecked,
      }));

      if (!isNowChecked) {
        setActiveFutureGames(prev =>
          prev.filter(game => game.leagueKey !== leagueKey)
        );
        return;
      }

      setLoading(true);


      const gamesData = (await futureGames({ league, future: futureArray })) ?? [];
      const leagueName = typeof league === "string" ? league : league?.name;

      const leagueLogo = await resolveGsUrl("gs://jocksportsdev.appspot.com/Logos/circle-list-svgrepo-com.svg");

      if (gamesData.length === 0 && !showLimitAlert) {
        setErrorMessage(` There are currently no games available for ${leagueName}`);
        setShowLimitAlert(true);
        setLastLeagueKey(leagueKey);
        return false;
      }

      if (gamesData.length === 0) {
        const emptyEntry = {
          id: `empty-${leagueKey}`,
          leagueKey,
          leagueLogo,
          isEmpty: true,
        } as any;

        setActiveFutureGames(prev => {
          if (prev.some(g => g.leagueKey === leagueKey)) return prev;
          return [...prev, emptyEntry];
        });

        return;
      }

      const gamesWithLogos: Future[] = gamesData.map(game => ({
        ...game,
        odds: filterPreferredSportsbookOdds(game.odds ?? []),
        leagueLogo,
        leagueKey,
        isEmpty: false,
      }));

      setActiveFutureGames(prev => [
        ...prev.filter(g => g.leagueKey !== leagueKey),
        ...gamesWithLogos,
      ]);
    } catch (error: any) {
      setError(error.message || "Error loading future games.");
    } finally {
      setLoading(false);
    }
  };

  const mapFutureGamesWithLogo = (
    gamesData: Future[],
    leagueKey: string,
    leagueLogo: string
  ): Future[] =>
    gamesData.map(game => ({
      ...game,
      odds: filterPreferredSportsbookOdds(game.odds ?? []),
      leagueLogo,
      leagueKey,
      isEmpty: false,
    }));

  /** Loads all PGA future markets for Commercial Live (Thu–Sun only). */
  const loadCommercialGolfFutures = async (options?: { silent?: boolean }) => {
    if (!isGolfFuturesLiveWindow()) {
      setCommercialFutureGames([]);
      return;
    }

    const golfSport =
      sports.find(s => s.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase()) ??
      sports.find(s => s.id?.toLowerCase() === "golf");
    const pgaLeague = golfSport?.leagues?.find(
      l => l.name?.toUpperCase() === PGA_LEAGUE_NAME
    );

    const categories = pgaLeague?.futureCategories ?? [];
    if (!pgaLeague || categories.length === 0) {
      setCommercialFutureGames([]);
      return;
    }

    if (!options?.silent) setLoading(true);

    try {
      const leagueLogo = await resolveGsUrl(
        "gs://jocksportsdev.appspot.com/Logos/circle-list-svgrepo-com.svg"
      );

      const results = await Promise.all(
        categories.flatMap(cat => {
          const itemsArray = Array.isArray(cat.items)
            ? cat.items
            : cat.items && typeof cat.items === "object"
              ? [cat.items]
              : [];

          return itemsArray.map(async (futureObj: any) => {
            const futureArray = Array.isArray(futureObj.selections)
              ? futureObj.selections
              : typeof futureObj.selections === "string"
                ? futureObj.selections.includes(",")
                  ? futureObj.selections.split(",").map((f: string) => f.trim())
                  : [futureObj.selections]
                : [];

            if (futureArray.length === 0) return [] as Future[];

            const leagueKey = `${GOLF_SPORT_NAME}-${pgaLeague.name}-${futureArray
              .join("_")
              .replace(/[, ]+/g, "_")}`;

            const gamesData =
              (await futureGames({ league: pgaLeague.name, future: futureArray })) ?? [];

            return mapFutureGamesWithLogo(gamesData, leagueKey, leagueLogo);
          });
        })
      );

      const merged = results.flat();
      const unique = new Map<string, Future>();
      merged.forEach(g => {
        const key = `${g.id}-${g.leagueKey}`;
        unique.set(key, g);
      });

      setCommercialFutureGames(Array.from(unique.values()));
    } catch (error: any) {
      console.error("Error loading commercial golf futures:", error);
      setError(error.message || "Error loading golf future games.");
    } finally {
      if (!options?.silent) setLoading(false);
    }
  };

  const clearCommercialGolfFutures = () => {
    setCommercialFutureGames([]);
  };

  const loadTeaserGame = async (sport: string, league: LeagueModel, teaserPoints: number, leagueKey: string,
    teaserType: string, isMain: boolean, startDateAfter?: string, startDateBefore?: string, timeOffset?: string) => {
    try {
      const marketKey = "";
      const marketArray: string[] = [];

      const isNowChecked = !(checkedTeaserLeagues[leagueKey] ?? false);

      setCheckedTeaserLeagues(prev => ({
        ...prev,
        [leagueKey]: isNowChecked
      }));

      if (!isNowChecked) {
        setActiveGamesTeaser(prev => prev.filter(game => game.leagueKey !== leagueKey));
        setNoDataTeaser(false);
        return;
      }

      setLoading(true);


      const gamesData = await scheduleLive({
        sport,
        league: league.name,
        marketId: marketArray.length > 0 ? marketArray : marketKey,
        isMain,
        startDateAfter, startDateBefore, timeOffset
      });

      const upcomingGames = (gamesData ?? []).filter(
        game => game.status === "unplayed" && !game.is_live
      );

      const markLeagueUnavailable = () => {
        setErrorMessage(`There are currently no games available for ${league.name}.`);
        setShowLimitAlert(true);
        setLastLeagueKey(leagueKey);
        setCheckedTeaserLeagues(prev => ({
          ...prev,
          [leagueKey]: false,
        }));
        setActiveGamesTeaser(prev => prev.filter(game => game.leagueKey !== leagueKey));
        setNoDataTeaser(true);
      };

      if (upcomingGames.length === 0) {
        markLeagueUnavailable();
        return;
      }

      const leagueLogo = league.logo || "";
      const gamesWithLogos: Schedule[] = upcomingGames.map(game => {
        const mainOdds = preferPrimarySportsbookOdds(game.odds?.filter(o => o.is_main) ?? []);

        const adjustedOdds = mainOdds.map(odd => applyTeaserPointsToOdd(odd, teaserPoints));

        return {
          ...game,
          leagueLogo,
          leagueKey,
          teaserPoints,
          teaserType,
          odds: adjustedOdds,
        };
      });

      setActiveGamesTeaser(prev => {
        const filtered = prev.filter(game => game.leagueKey !== leagueKey);

        return [...filtered, ...gamesWithLogos];
      });
      setNoDataTeaser(false);
    } catch (error: any) {
      setActiveGamesTeaser(prev => prev.filter(game => game.leagueKey !== leagueKey));
      setCheckedTeaserLeagues(prev => ({
        ...prev,
        [leagueKey]: false,
      }));
      setNoDataTeaser(true);
      setErrorMessage(`There are currently no games available for ${league.name}.`);
      setShowLimitAlert(true);
      setLastLeagueKey(leagueKey);
      setError(error.message || "Error loading games.");
    } finally {
      setLoading(false);
    }
  };


  const loadConfig = async () => {
    try {
      if (firebaseUser) {
        await firebaseUser.getIdToken();
      }
      const config = await mainScreen();
      if (!config) return;

      setMarketOrderFromDB(config.slots ?? []);
      setLeagueLiveconfig(config.lives ?? []);
      const banners = config.banners ?? [];
      setBannerSportConfig(banners);
      try {
        localStorage.setItem("bannerSportConfig", JSON.stringify(banners));
      } catch {
        // localStorage no disponible o cuota llena
      }
    } catch (error) {
      console.error("Error loading config", error);
    } finally {
      setConfigLoaded(true);
    }
  };

  //por si tengo que eliminarlo solo dejar loadConfig
  useEffect(() => {
    if (authLoading) return;

    if (!firebaseUser || !user) {
      setConfigLoaded(true);
      return;
    }

    loadConfig();
  }, [firebaseUser, user, authLoading]);


  const refreshSelectedGames = async () => {
    if (!sports) return;

    setLoading(true);

    try {
      const activeLeagueKeys = Object.entries(checkedLeagues)
        .filter(([_, isChecked]) => isChecked)
        .map(([key]) => key);

      await Promise.all(
        activeLeagueKeys.map(async (leagueKey) => {
          const parts = leagueKey.split("-");

          const sportName = parts[0];
          const leagueName = parts[1];
          const marketKey = parts[2] || "";

          const sport = sports.find(s => s.name === sportName);
          const league = sport?.leagues?.find(l => l.name === leagueName);
          const savedMarkets = marketsByLeagueKey[leagueKey] || [];

          if (!sport || !league) return;

          const { startDateAfter, startDateBefore } = getScheduleDateRange({
            extraDays: PREGAME_SCHEDULE_EXTRA_DAYS,
            league,
            market: findLeagueMarket(league, marketKey),
          });
          return loadGames(
            sportName,
            league,
            marketKey,
            savedMarkets,
            undefined,
            undefined,
            startDateAfter,
            startDateBefore,
            true
          );
        })
      );

    } catch (error) {
      console.error("Error refreshing games:", error);
    } finally {
      setLoading(false);
    }
  };


  const data = {
    //for class SportSchedule
    showOptions,
    setShowOptions,
    sports,
    loading,
    error,
    loadSports,
    toggleOptions,
    loadGames,
    //for class ScheduleTable
    activeGames,
    checkedLeagues,
    selectedGames,
    setSelectedGames,
    loadTeaserGame,
    activeGamesTeaser,
    setActiveGamesTeaser,
    boxesRef,
    setCheckedLeagues: setCheckedLeaguesSafe,
    checkedLeaguesRef,
    setActiveGames,
    loadGamesFuture,
    loadCommercialGolfFutures,
    clearCommercialGolfFutures,
    activeFutureGames,
    commercialFutureGames,
    isParlay, setIsParlay,
    activeFutureIndividual, setActiveFutureIndividual,
    showSports, setShowSports,
    showHomeButton, setShowHomeButton,
    commercialGames, setCommercialGames,
    commercialLeagueOrder,
    setActiveFutureGames,
    marketsByLeague,
    gameEspecial,
    setGameEspecial,
    leagueLive,
    loadLiveEvents,
    marketOrderFromDB, setMarketOrderFromDB,
    configLoaded,
    leagueLiveconfig, setLeagueLiveconfig,
    loadConfig,
    checkedTeaserLeagues, setCheckedTeaserLeagues,
    noDataTeaser, setNoDataTeaser,
    selectedGamesTeaser, setSelectedGamesTeaser,
    errorMessages, showLimitAlert,
    setShowLimitAlert,
    handleCloseLimitAlert,
    refreshSelectedGames,
    bannerSportConfig
  };

  return (
    <SportContext.Provider value={data}>
      {children}
    </SportContext.Provider>
  )
}
export { SportProvider };
export default SportContext;

export const useSport = () => {
  const context = React.useContext(SportContext);
  if (!context) {
    throw new Error("useSport must be used within UserProvider");
  }
  return context;
};